import * as React from 'react';
import { observer } from 'mobx-react';
import { serviceNames, diContainer } from '../../../service-setup/diContainer';
import { translate, config } from 'elc-service';
import styled from 'styled-components';
import { ProductInstallmentsPriceViewController } from '../../controllers/ProductInstallmentsPriceViewController';
import { IframeModal } from 'elc-responsive-modal';
import { ContentSpacing, breakpoint } from 'elc-base-theme';
import { InfoIcon } from 'elc-icons';
import { AFTER_PAY } from '../../constants/DataTestIdAttributes';
import { classes } from '../../utils/Classes';
import { Button } from 'elc-buttons';
import { ComponentTypes } from '../../constants/ComponentTypes';
import { IComponentConfigByType } from '../../interfaces/IProdcat';

export interface IProductInstallmentsPriceLabels {
    installmentsModalLabel: string;
    installmentsLabel?: string;
    installmentsUnavailableLabel?: string;
    openLabel?: string;
}

export interface IProductInstallmentsViewConfig {
    enabled?: boolean;
    numberOfInstallments?: number;
    installmentsMinimumPrice?: number;
    installmentsLogoImageURL?: string;
    installmentsiFrameURL?: string;
}

export interface IProductInstallmentsConfig extends IProductInstallmentsViewConfig {
    overrides?: IComponentConfigByType;
}

export interface IProductInstallmentsPrice {
    className?: string;
    translations?: IProductInstallmentsPriceLabels;
    defaultPrice: number;
    config?: {
        installments?: IProductInstallmentsConfig;
    };
    componentType: ComponentTypes;
}

const InstallmentsIFrame = styled(IframeModal)`
    padding: 0;
    width: 100%;
    height: 100%;
    ${breakpoint('desktop')`
        max-width: 774px;
        max-height: 600px;
    `};
`;

const StyledInfoIcon = styled(InfoIcon)`
    width: ${ContentSpacing.space16};
    height: ${ContentSpacing.space16};
    vertical-align: middle;
    margin-left: ${ContentSpacing.space4};
    cursor: pointer;

    [dir='rtl'] & {
        margin-left: 0;
        margin-right: ${ContentSpacing.space4};
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: ${ContentSpacing.space20} ${ContentSpacing.space10} 0;
`;

@config(['installments'])
@translate([
    'installmentsLabel',
    'installmentsUnavailableLabel',
    'installmentsModalLabel',
    'openLabel'
])
@observer
export class ProductInstallmentsPrice extends React.Component<IProductInstallmentsPrice> {
    private viewController: ProductInstallmentsPriceViewController;
    constructor(props: IProductInstallmentsPrice) {
        super(props);
        this.viewController = diContainer.get(serviceNames.productInstallmentsPriceViewController);
        this.viewController.initialize(props);
    }

    private openInstallmentsModal = () => {
        this.viewController.openModal();
    };

    private closeInstallmentsModal = () => {
        this.viewController.closeModal();
    };

    private productInstallmentsClassNames = classes(
        'product-installments-price',
        this.props.className
    );

    public render() {
        const {
            getFormattedPrice,
            setIntervalPrice,
            data: { isInstallmentsModalOpen, componentConfig }
        } = this.viewController;

        const {
            enabled,
            numberOfInstallments,
            installmentsMinimumPrice,
            installmentsLogoImageURL,
            installmentsiFrameURL
        } = componentConfig as IProductInstallmentsViewConfig;

        if (!enabled) {
            return null;
        }

        const { defaultPrice } = this.props;

        const {
            installmentsLabel,
            installmentsUnavailableLabel,
            installmentsModalLabel,
            openLabel
        } = this.props.translations as IProductInstallmentsPriceLabels;

        const logoImgInstallmentsClassNames = classes('product-installments-price-logo');
        const openModalClassNames = classes('product-installments-price-open-modal');
        const installmentsLabelClassNames = classes('product-installments-price-label');
        const installmentsUnavailableLabelClassNames = classes(
            'product-installments-price-unavailable-label'
        );
        const iFrameInstallmentsClassNames = classes('product-installments-price-iframe');
        const iFrameInstallmentsMaskClassNames = classes('product-installments-iframe-mask');

        const intervalPrice =
            numberOfInstallments && setIntervalPrice(defaultPrice, numberOfInstallments);
        const intervalPriceFormatted = intervalPrice && getFormattedPrice(intervalPrice);
        const installmentsLabelFormatted =
            (enabled &&
                numberOfInstallments &&
                intervalPriceFormatted &&
                installmentsLabel &&
                installmentsLabel
                    .replace('::INTERVALS::', numberOfInstallments.toString())
                    .replace('::PRICE::', intervalPriceFormatted)) ||
            '';

        const minimumInstallmentsPriceFormatted =
            installmentsMinimumPrice && getFormattedPrice(installmentsMinimumPrice);
        const installmentsUnavailableLabelFormatted =
            minimumInstallmentsPriceFormatted &&
            installmentsUnavailableLabel &&
            installmentsUnavailableLabel.replace('::PRICE::', minimumInstallmentsPriceFormatted);

        return (
            <ContentWrapper className={this.productInstallmentsClassNames} data-test-id={AFTER_PAY}>
                {installmentsLogoImageURL && (
                    <img
                        alt={installmentsLabelFormatted}
                        src={installmentsLogoImageURL}
                        className={logoImgInstallmentsClassNames}
                    />
                )}
                {installmentsiFrameURL && (
                    <Button
                        onClick={this.openInstallmentsModal}
                        className={openModalClassNames}
                        aria-label={`${openLabel} ${installmentsModalLabel}`}
                        title={`${openLabel} ${installmentsModalLabel}`}
                    >
                        <StyledInfoIcon />
                    </Button>
                )}
                {installmentsMinimumPrice && defaultPrice > installmentsMinimumPrice ? (
                    <span className={installmentsLabelClassNames}>
                        {installmentsLabelFormatted}
                    </span>
                ) : (
                    <span className={installmentsUnavailableLabelClassNames}>
                        {installmentsUnavailableLabelFormatted}
                    </span>
                )}

                {installmentsiFrameURL && (
                    <InstallmentsIFrame
                        isOpen={isInstallmentsModalOpen}
                        onMaskClick={this.closeInstallmentsModal}
                        title={installmentsModalLabel}
                        url={installmentsiFrameURL}
                        closeModal={this.closeInstallmentsModal}
                        iframeClassName={iFrameInstallmentsClassNames}
                        maskClassName={iFrameInstallmentsMaskClassNames}
                    />
                )}
            </ContentWrapper>
        );
    }
}

export default ProductInstallmentsPrice;
